/* assets/registered-product.js */
(() => {
    // =========================================================
    // Helpers
    // =========================================================
    function qs(id) {
        return document.getElementById(id);
    }
    function qsa(sel, root = document) {
        return Array.from(root.querySelectorAll(sel));
    }
    function str(v) {
        return v == null ? "" : String(v);
    }
    function safeJsonParse(text) {
        try {
            return JSON.parse(text);
        } catch {
            return null;
        }
    }
    function formatTime(isoOrDate) {
        try {
            const d = isoOrDate ? new Date(isoOrDate) : new Date();
            return d.toLocaleString(undefined, { hour: "numeric", minute: "2-digit" });
        } catch {
            return "";
        }
    }

    // =========================================================
    // UI: Swap row actions
    // =========================================================
    function swapRowToContact(actionBox) {
        if (!actionBox) return;

        const view = actionBox.querySelector(".wss-view-parts");
        const contact = actionBox.querySelector(".wss-contact");
        const msg = actionBox.querySelector(".wss-unavailable");

        if (view) view.style.display = "none";
        if (contact) contact.style.display = "inline-block";
        if (msg) msg.style.display = "block";
    }

    function keepRowAsViewParts(actionBox) {
        if (!actionBox) return;

        const view = actionBox.querySelector(".wss-view-parts");
        const contact = actionBox.querySelector(".wss-contact");
        const msg = actionBox.querySelector(".wss-unavailable");

        if (view) view.style.display = "inline-block";
        if (contact) contact.style.display = "none";
        if (msg) msg.style.display = "none";
    }

    // =========================================================
    // Normalize URL (fix encoded/double pages)
    // =========================================================
    function normalizePartsUrl(rawUrl) {
        const s = String(rawUrl || "").trim();
        if (!s) return "";

        // If whole path was encoded, decode it.
        if (s.includes("%2Fpages%2F") || s.includes("%2Fpages%2Fbom-product%2F")) {
            try {
                const decoded = decodeURIComponent(s);
                return decoded
                    .replace("/pages//pages/", "/pages/")
                    .replace("//pages/", "/pages/");
            } catch {
                return s;
            }
        }

        return s;
    }

    // =========================================================
    // SKU existence cache (1 week)
    // =========================================================
    const SKU_CACHE_TTL = 7 * 24 * 60 * 60 * 1000;

    function cacheKey(shop, sku) {
        return `wss_parts_exists:${shop || "unknown"}:${sku}`;
    }

    function cacheGet(shop, sku) {
        try {
            const raw = localStorage.getItem(cacheKey(shop, sku));
            if (!raw) return null;

            const parsed = JSON.parse(raw);
            if (!parsed || typeof parsed !== "object") return null;

            if (Date.now() > parsed.expiresAt) {
                localStorage.removeItem(cacheKey(shop, sku));
                return null;
            }

            return parsed.value === 1 ? true : parsed.value === 0 ? false : null;
        } catch {
            return null;
        }
    }

    function cacheSet(shop, sku, exists) {
        try {
            localStorage.setItem(
                cacheKey(shop, sku),
                JSON.stringify({
                    value: exists ? 1 : 0,
                    expiresAt: Date.now() + SKU_CACHE_TTL,
                })
            );
        } catch {
            // ignore
        }
    }

    // =========================================================
    // Check SKU existence (uses your product-search endpoint)
    // =========================================================
    async function checkSkuExists({ sku, shop, searchEndpoint }) {
        const s = String(sku || "").trim();
        if (!s || s.length < 2) return false;

        // 1) cache
        const cached = cacheGet(shop, s);
        if (cached !== null) return cached;

        // 2) API call
        const params = new URLSearchParams();
        params.set("q", s);
        params.set("limit", "1");
        params.set("by", "sku");
        if (shop) params.set("shop_domain", shop);

        const url = `${searchEndpoint}?${params.toString()}`;

        try {
            const res = await fetch(url, {
                method: "GET",
                headers: { Accept: "application/json" },
                credentials: "same-origin",
            });

            const txt = await res.text().catch(() => "");
            const json = safeJsonParse(txt);

            if (!res.ok || !json || json.ok === false) {
                cacheSet(shop, s, false);
                return false;
            }

            const exists = Number(json.count || 0) > 0;
            cacheSet(shop, s, exists);
            return exists;
        } catch {
            // no cache write on network error
            return false;
        }
    }

    // =========================================================
    // Optional: Validate BOM page exists (404 or fake-200 detection)
    // =========================================================
    async function validateBomPage({ url, marker }) {
        const u = normalizePartsUrl(url);
        if (!u) return false;

        try {
            // HEAD is faster but Shopify can behave differently per theme.
            // Use GET to be safest.
            const res = await fetch(u, {
                method: "GET",
                credentials: "same-origin",
                headers: { Accept: "text/html" },
            });

            if (res.status === 404) return false;
            if (!res.ok) return false;

            // Optional marker check (detect “fake 200 not found”)
            if (marker) {
                const html = await res.text().catch(() => "");
                const low = (html || "").toLowerCase();

                const looksMissing =
                    !html ||
                    low.includes("page not found") ||
                    low.includes("not found") ||
                    low.includes("404") ||
                    !html.includes(marker);

                if (looksMissing) return false;
            }

            return true;
        } catch {
            return false;
        }
    }

    // =========================================================
    // Validate all “View Parts” links
    // Strategy:
    // 1) Determine SKU from the URL last segment
    // 2) Check SKU existence via product-search endpoint
    // 3) If exists -> optionally verify BOM page URL returns real content
    // =========================================================
    async function validatePartsLinks(rootEl) {
        const root = rootEl || document;
        const links = qsa(".wss-view-parts[data-url]", root);
        if (!links.length) return;

        const shop = rootEl?.dataset?.shop || rootEl?.dataset?.shopDomain || "";
        const searchEndpoint = rootEl?.dataset?.productSearchEndpoint || "/apps/wss/product-search";
        const marker =
            rootEl?.dataset && typeof rootEl.dataset.partsMarker === "string"
                ? rootEl.dataset.partsMarker.trim()
                : "";

        const MAX = 4;
        let idx = 0;

        async function worker() {
            while (idx < links.length) {
                const i = idx++;
                const link = links[i];

                const rawUrl = link.dataset.url || link.getAttribute("href") || "";
                const url = normalizePartsUrl(rawUrl);

                const actionBox = link.closest(".wss-action");
                if (!actionBox) continue;

                // Default: keep View Parts visible until proven invalid
                keepRowAsViewParts(actionBox);

                // Extract SKU/model from last path segment
                const last = (url.split("/").pop() || "").trim();
                const sku = decodeURIComponent(last || "").trim();

                if (!sku) {
                    swapRowToContact(actionBox);
                    continue;
                }

                // 1) SKU exists?
                const skuExists = await checkSkuExists({ sku, shop, searchEndpoint });
                if (!skuExists) {
                    swapRowToContact(actionBox);
                    continue;
                }

                // 2) BOM page exists?
                const bomOk = await validateBomPage({ url, marker });
                if (!bomOk) {
                    swapRowToContact(actionBox);
                    continue;
                }

                // Valid -> keep as View Parts
                keepRowAsViewParts(actionBox);
            }
        }

        const workers = [];
        for (let w = 0; w < Math.min(MAX, links.length); w++) workers.push(worker());
        await Promise.all(workers);
    }

    // =========================================================
    // Warranty Refresh UI
    // =========================================================
    function initWss() {
        const root = qs("wss-root");
        if (!root) return;

        if (root.dataset.initialized === "true") return;
        root.dataset.initialized = "true";

        const baseEndpoint = root.dataset.endpoint || "";
        const email = root.dataset.email || "";
        const sessionKey = root.dataset.sessionKey || "wss_auto_refreshed";

        if (!baseEndpoint || !email) return;

        const endpoint = `${baseEndpoint}?email=${encodeURIComponent(email)}`;

        const statusEl = qs("wss-status");
        const statusTextEl = qs("wss-status-text");
        const refreshBtn = qs("wss-refresh");

        if (!statusEl || !statusTextEl || !refreshBtn) return;

        function setState(className, text) {
            statusEl.classList.remove("is-loading", "is-success", "is-error");
            if (className) statusEl.classList.add(className);
            statusTextEl.textContent = text;
        }

        function disableRefresh(disabled) {
            refreshBtn.disabled = !!disabled;
        }

        async function callApi({ reloadOnSuccess = false, markSession = false } = {}) {
            setState("is-loading", "Refreshing warranty data…");
            disableRefresh(true);

            try {
                const res = await fetch(endpoint, {
                    headers: { Accept: "application/json" },
                    credentials: "same-origin",
                });

                const bodyText = await res.text().catch(() => "");
                const payload = safeJsonParse(bodyText);

                if (!res.ok) {
                    setState("is-error", `Request failed (${res.status}). Please try again.`);
                    disableRefresh(false);
                    return;
                }

                if (!payload) {
                    setState("is-error", "Unexpected response (not JSON).");
                    disableRefresh(false);
                    return;
                }

                if (payload.success) {
                    const timeLabel = payload.refreshedAt ? formatTime(payload.refreshedAt) : formatTime(new Date());
                    setState("is-success", `Warranty data updated at ${timeLabel}`);
                    disableRefresh(false);

                    if (markSession) sessionStorage.setItem(sessionKey, "done");
                    if (reloadOnSuccess) setTimeout(() => window.location.reload(), 650);
                    return;
                }

                setState("is-error", payload.message || "Couldn’t refresh. Please try again.");
                disableRefresh(false);
            } catch {
                setState("is-error", "Network error. Please try again.");
                disableRefresh(false);
            }
        }

        refreshBtn.addEventListener("click", () => {
            sessionStorage.removeItem(sessionKey);
            callApi({ reloadOnSuccess: true, markSession: true });
        });

        if (!sessionStorage.getItem(sessionKey)) {
            callApi({ reloadOnSuccess: true, markSession: true });
        } else {
            setState("", "Warranty data is up to date. You can refresh anytime.");
            disableRefresh(false);
        }

        // Validate parts links after render (does not block UI)
        validatePartsLinks(root).catch(() => {});
    }

    // Normal load
    document.addEventListener("DOMContentLoaded", initWss);

    // Theme editor section reload support
    document.addEventListener("shopify:section:load", () => {
        const root = qs("wss-root");
        if (root) root.dataset.initialized = "false";
        initWss();
    });
})();
